import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import Notifications from './pages/Notifications';
import Subscriptions from './pages/Subscriptions';
import LiveStreams from './pages/LiveStreams';
import LiveMonitor from './pages/LiveMonitor';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';


import Sidebar from './components/Sidebar';
import Header from './components/Header';
import NotificationListener from './components/NotificationListener';


import Centres from './pages/Centres';
import Vendors from './pages/Vendors';
import Products from './pages/Products';
import Orders from './pages/Orders';
import Users from './pages/Users';
import DeleteAccountRequests from './pages/DeleteAccountRequests';
import PagesManager from './pages/PagesManager';
import MetaSettings from './pages/MetaSettings';
import VendorDetails from './pages/VendorDetails';
import VendorWallet from './pages/VendorWallet';
import VendorPayouts from './pages/VendorPayouts';
import VendorRequests from './pages/VendorRequests';
import CentreRequests from './pages/CentreRequests'; // Centre onboarding requests
import AcaryaRequests from './pages/AcaryaRequests'; // Acarya onboarding requests
import Login from './pages/Login';
import VendorRegister from './pages/VendorRegister'; // Vendor Registration
import CentreRegister from './pages/CentreRegister'; // Centre Onboarding Registration
import AcaryaRegister from './pages/AcaryaRegister'; // Acarya/Acarya Onboarding Registration
import ForgotPassword from './pages/ForgotPassword'; // Forgot Password


import Categories from './pages/Categories';
import EventsManager from './pages/EventsManager';
import SevaReportsManager from './pages/SevaReportsManager';
import EbooksManager from './pages/EbooksManager';
import AudioManager from './pages/AudioManager';
import CeremonyServices from './pages/CeremonyServices';
import SevaOffering from './pages/SevaOffering';
import Donations from './pages/Donations';
import Offerings from './pages/Offerings';
import CentreShop from './pages/CentreShop';
import HeroManager from './pages/HeroManager';
import ReelsStudio from './pages/ReelsStudio';
import AcaryasManager from './pages/AcaryasManager';
import ConsultationInbox from './pages/ConsultationInbox';
import AcaryaRevenue from './pages/AcaryaRevenue';
import AcaryaSlots from './pages/AcaryaSlots';
import AcaryaProfile from './pages/AcaryaProfile';
import CentreRevenue from './pages/CentreRevenue';
import Dashboard from './pages/Dashboard';
import PlatformAnalytics from './pages/PlatformAnalytics';
import CentreDashboard from './pages/CentreDashboard';

// Dashboard selection logic based on role
const DashboardWrapper = () => {
  const { role } = useAuth();

  if (role === 'ACARYA') return <Navigate to="/consultations/inbox" replace />;
  if (role === 'CENTRE') return <CentreDashboard />;
  // ADMIN, SUPER_ADMIN, and VENDOR default to dynamic Dashboard
  return <Dashboard />;
};


const AppContent = () => {
  const location = useLocation();
  const hideSidebar = ['/login', '/vendor-register', '/centre-register', '/acarya-register', '/forgot-password'].includes(location.pathname);

  return (
    <div className="bg-transparent text-ivory font-body-md text-base antialiased min-h-screen flex">
      {/*  */}
      {!hideSidebar && <Sidebar />}

      <main className={`${!hideSidebar ? 'ml-64' : ''} flex-1 flex flex-col min-h-screen`}>
        {/*  */}
        {!hideSidebar && <Header />}
        <NotificationListener />

        <Routes>
          {/* */}
          <Route path="/login" element={<Login />} />
          <Route path="/vendor-register" element={<VendorRegister />} />
          {/* Separate onboarding portal for centre trusts/institutions */}
          <Route path="/centre-register" element={<CentreRegister />} />
          <Route path="/acarya-register" element={<AcaryaRegister />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/*  */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/analytics" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><PlatformAnalytics /></PrivateRoute>} />
          <Route path="/dashboard" element={
            <PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'ACARYA', 'CENTRE']}>
              <DashboardWrapper />
            </PrivateRoute>
          } />
          <Route path="/centres" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><Centres /></PrivateRoute>} />
          <Route path="/vendor-requests" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><VendorRequests /></PrivateRoute>} />
          {/* Separate Centre Requests sub-tab per role-CENTRE.md spec */}
          <Route path="/centre-requests" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><CentreRequests /></PrivateRoute>} />
          <Route path="/acarya-requests" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><AcaryaRequests /></PrivateRoute>} />
          <Route path="/vendors" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><Vendors /></PrivateRoute>} />
          <Route path="/vendors/:id" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><VendorDetails /></PrivateRoute>} />
          <Route path="/wallet" element={<PrivateRoute allowedRoles={['VENDOR', 'CENTRE', 'ACARYA']}><VendorWallet /></PrivateRoute>} />
          <Route path="/admin/payouts" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><VendorPayouts /></PrivateRoute>} />
          <Route path="/products" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><Products /></PrivateRoute>} />
          <Route path="/orders" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><Orders /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><Users /></PrivateRoute>} />
          <Route path="/delete-account-requests" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><DeleteAccountRequests /></PrivateRoute>} />
          <Route path="/pages" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><PagesManager /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><MetaSettings /></PrivateRoute>} />
          <Route path="/live-streams" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><LiveStreams /></PrivateRoute>} />
          <Route path="/live-monitor/:streamId" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><LiveMonitor /></PrivateRoute>} />

          {/*  */}
          <Route path="/categories" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><Categories /></PrivateRoute>} />
          <Route path="/events" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><EventsManager /></PrivateRoute>} />
          <Route path="/seva-reports" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><SevaReportsManager /></PrivateRoute>} />
          <Route path="/ebooks" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><EbooksManager /></PrivateRoute>} />
          <Route path="/audio-library" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><AudioManager /></PrivateRoute>} />
          <Route path="/ceremonies" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><CeremonyServices /></PrivateRoute>} />
          <Route path="/sevaOffering" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><SevaOffering /></PrivateRoute>} />
          <Route path="/donations" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><Donations /></PrivateRoute>} />
          <Route path="/offerings" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'CENTRE']}><Offerings /></PrivateRoute>} />
          <Route path="/centre-shop" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><CentreShop /></PrivateRoute>} />
          <Route path="/notifications" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><Notifications /></PrivateRoute>} />
          <Route path="/subscriptions" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'VENDOR', 'CENTRE']}><Subscriptions /></PrivateRoute>} />

          <Route path="/hero" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><HeroManager /></PrivateRoute>} />
          <Route path="/reels" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><ReelsStudio /></PrivateRoute>} />
          <Route path="/acaryas" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}><AcaryasManager /></PrivateRoute>} />
          <Route path="/consultations/inbox" element={<PrivateRoute allowedRoles={['ACARYA']}><ConsultationInbox /></PrivateRoute>} />
          <Route path="/acarya/revenue" element={<PrivateRoute allowedRoles={['ACARYA']}><AcaryaRevenue /></PrivateRoute>} />
          <Route path="/acarya/slots" element={<PrivateRoute allowedRoles={['ACARYA']}><AcaryaSlots /></PrivateRoute>} />
          <Route path="/acarya/profile" element={<PrivateRoute allowedRoles={['ACARYA']}><AcaryaProfile /></PrivateRoute>} />
          <Route path="/centre/revenue" element={<PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'CENTRE']}><CentreRevenue /></PrivateRoute>} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;